import { ROUTES, withPeek } from "@/app/routes";
import type { Notification } from "@/domain";

/**
 * Generates navigation link for notification's related entity.
 * Must stay in sync with backend `RelatedEntityType` (domain/notifications/enums.py).
 */
export const getNotificationLink = (notification: Notification): string | null => {
  if (!notification.related_entity_type) {
    return null;
  }

  /**
   * A completed team invitation leaves no record of its own, so it is the one
   * entity with nothing to address: the members page is where somebody joining
   * shows up.
   */
  if (notification.related_entity_type === "organization") {
    return ROUTES.ORGANIZATION.USERS;
  }

  if (!notification.related_entity_uuid) {
    return null;
  }

  const uuid = notification.related_entity_uuid;
  if (notification.related_entity_type === "note") {
    const context = notification.related_context;
    const holder = context?.holder_uuid;
    if (!holder) return null;
    let path: string | null = null;
    switch (context?.holder_type) {
      case "beneficiary":
        path = ROUTES.BENEFICIARIES.DETAIL(holder);
        break;
      case "investor":
        path = ROUTES.INVESTORS.DETAIL(holder);
        break;
      case "investment":
        path = ROUTES.INVESTMENTS.DETAIL(holder);
        break;
      case "fiscal_quota":
        if (context.beneficiary_uuid)
          path = ROUTES.BENEFICIARIES.QUOTA(context.beneficiary_uuid, holder);
        break;
      case "project":
        if (context.beneficiary_uuid)
          path = withPeek(ROUTES.BENEFICIARIES.DETAIL(context.beneficiary_uuid), holder);
        break;
    }
    return path
      ? `${path}${path.includes("?") ? "&" : "?"}tab=notes&nota=${encodeURIComponent(uuid)}`
      : null;
  }
  if (notification.related_entity_type === "comment_thread") {
    const context = notification.related_context;
    const subject = context?.subject_uuid;
    if (!subject) return null;
    let path: string | null = null;
    switch (context?.subject_type) {
      case "beneficiary":
        path = withPeek(ROUTES.BENEFICIARIES.LIST, subject);
        break;
      case "investor":
        path = withPeek(ROUTES.INVESTMENTS.INVESTORS, subject);
        break;
      case "investment":
        path = withPeek(ROUTES.INVESTMENTS.MAIN, subject);
        break;
      case "document":
        path = withPeek(ROUTES.INVESTMENTS.CONTRACTS, subject);
        break;
      case "fiscal_quota":
        path = withPeek(ROUTES.QUOTAS, subject);
        break;
      case "template":
        path = ROUTES.ORGANIZATION.TEMPLATE_DETAIL(subject);
        break;
      case "project":
        if (context.beneficiary_uuid)
          path = `${withPeek(ROUTES.BENEFICIARIES.LIST, context.beneficiary_uuid)}&project=${encodeURIComponent(subject)}`;
        break;
      case "classification":
        if (context.beneficiary_uuid && context.fiscal_quota_uuid)
          path = withPeek(
            ROUTES.BENEFICIARIES.AUDIT(context.beneficiary_uuid, context.fiscal_quota_uuid),
            subject,
          );
        break;
    }
    return path
      ? `${path}${path.includes("?") ? "&" : "?"}thread=${encodeURIComponent(uuid)}`
      : null;
  }
  switch (notification.related_entity_type) {
    case "project":
      return ROUTES.PROJECTS.DETAIL(encodeURIComponent(uuid));
    case "beneficiary":
      return ROUTES.BENEFICIARIES.DETAIL(encodeURIComponent(uuid));
    case "investment":
      return withPeek(ROUTES.INVESTMENTS.MAIN, uuid);
    /**
     * "document" is a legacy alias — contract notifications were stored as
     * "document" before the 2026-05-20 migration. Treated the same as
     * "contract" so historical notifications still route correctly.
     */
    case "contract":
    case "document":
      return withPeek(ROUTES.INVESTMENTS.CONTRACTS, uuid);
    case "investor":
      return withPeek(ROUTES.INVESTMENTS.INVESTORS, uuid);
    /**
     * A quota lives under its promoter, so one uuid cannot address it: the
     * backend hands both in `related_context` (E11-backend). Without the
     * promoter there is nowhere to go.
     */
    case "fiscal_quota": {
      const beneficiary = notification.related_context?.beneficiary_uuid;
      return beneficiary ? ROUTES.BENEFICIARIES.QUOTA(beneficiary, uuid) : null;
    }
    /**
     * A proposal lives under its investor, so one uuid cannot address it
     * either: the backend hands both in `related_context` (E10-backend).
     * Without the investor there is nowhere to go.
     */
    case "investment_proposal": {
      const investor = notification.related_context?.investor_uuid;
      return investor ? ROUTES.INVESTORS.PROPOSAL(investor, uuid) : null;
    }
    default:
      return null;
  }
};
